import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { useRosterLookup } from "../../hooks/useRosterLookup";
import { useScheduleWeeks } from "../../hooks/useScheduleWeeks";
import { INK, NEUTRAL, SHIFT_NIGHT_FALLBACK, SURFACE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { deepCloneObject } from "../../utils/dateUtils";
import { scheduledShiftsByIdOf } from "../../utils/departmentWeeks";
import { type NurseInShift, NurseTrainingStatus, ShiftType } from "../../utils/models";
import { inverseRolesMap } from "../../utils/utilFunctions";
import { EditTimePopover } from "../EditTimePopover";
import { PanelDayFallback } from "../StaffingChanges/PanelDayFallback";
import { MobileNurseItem } from "../staffing/mobile/NursesList/MobileNurseItem";
import { CommonHeader } from "../UIToolkit/molecules/CommonHeader";
import { EmptyState } from "../UIToolkit/molecules/EmptyState";
import { JobSelector } from "../UIToolkit/molecules/JobSelector";
import { ShiftDataNurseRemoval } from "./ShiftDataNurseRemoval";
import { ShiftDataRoomList } from "./ShiftDataRoomList";

const PAGE_SX = {
	bgcolor: SURFACE,
	maxWidth: "560px",
	margin: "0 auto",
	borderRadius: "8px",
	boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
	display: "flex",
	flexDirection: "column",
	p: 1,
	height: "100%",
	overflowY: "auto",
} as const;

/**
 * The phone's Edit Shift page. The shift is found by id in the weeks around the link's `date` (`useScheduleWeeks`):
 * the schedule cache with `staffing_schedule_cache` on, every loaded week of the old list off, as before.
 */
export const ShiftData: FC = () => {
	const { id } = useParams();
	const [searchParams] = useSearchParams();
	const dateParam = searchParams.get("date");
	const { chosenDepartment } = useHospitalData();
	// DEFAULT: a link from before the page took a date names none, and the weeks around today are searched; with the
	// cache off every loaded week is searched whatever the date.
	const date = useMemo(
		() => (dateParam === null ? DateTime.now() : DateTime.fromISO(dateParam, { zone: chosenDepartment.timezone })),
		[dateParam, chosenDepartment.timezone],
	);
	const schedule = useScheduleWeeks(date);
	const shift = useMemo(() => (id === undefined ? undefined : scheduledShiftsByIdOf(schedule.weeks).get(id)), [id, schedule.weeks]);
	if (schedule.isLoading || schedule.isError) return <PanelDayFallback panelDay={schedule} dataTestId="shift-data" />;
	if (shift === undefined) return <ShiftDataNotFound id={id} />;
	// Keyed by the shift, so the form starts from its values once it is found.
	return <ShiftDataForShift key={shift.id} shift={shift} />;
};

/** No shift with the link's id in the weeks around its day: removed, or moved to a day a week or more away. */
const ShiftDataNotFound: FC<{ id: string | undefined }> = ({ id }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { chosenDepartment } = useHospitalData();
	const returnTo = searchParams.get("returnTo");

	useEffect(() => {
		trackEvent("shift_data_shift_not_found", { nurse_in_shift_id: id });
	}, [id]);

	return (
		<Box sx={PAGE_SX}>
			<CommonHeader title={"Edit Shift"} onClose={() => navigate(returnTo ?? `/${chosenDepartment.department_id}/staffing`)} />
			<EmptyState dataTestId="shift-data-not-found" title={t("shift.data.not.found.title")} hint={t("shift.data.not.found.hint")} sx={{ py: 8, px: 3 }} />
		</Box>
	);
};

const ShiftDataForShift: FC<{ shift: NurseInShift }> = ({ shift }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const returnTo = searchParams.get("returnTo");
	const { showErrorSnackbar, updateNurseTimes, chosenDepartment, scheduleDayOf, afterScheduleWrite } = useHospitalData();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const { showSnackbar } = useSnackbar();
	const { updateNurse, isLoading } = useHandleNurseChange();
	const [isEditingTime, setIsEditingTime] = useState<boolean>(false);
	const [anchorRef, setAnchorRef] = useState<HTMLElement>();

	const shiftNo: number = shift.shiftType === ShiftType.Day ? 0 : 1;

	// DEFAULT: a shift with no location starts the picker on none.
	const [selectedRoom, setSelectedRoom] = useState<string>(shift.location ?? "");
	const [shiftStart, setShiftStart] = useState<DateTime>(shift.startTime);
	const [shiftEnd, setShiftEnd] = useState<DateTime>(shift.endTime);
	const [isCharge, setIsCharge] = useState<boolean>(shift.charge);
	const [absenceMessage, setAbsenceMessage] = useState<string>("");
	const [isTrainee, setIsTrainee] = useState<boolean>(shift.trainingStatus === NurseTrainingStatus.Trainee);
	const [selectedJobId, setSelectedJobId] = useState<string | undefined>(shift.jobId);

	const nurse = getNurseById(shift.nurseId);
	const hasMultipleJobs = (nurse?.jobs?.length ?? 0) > 1;

	const isRemoveButtonEnabled = !!absenceMessage && !(isLoading.removeNurse || isLoading.updateNurse);

	const getReturnPath = (): string => returnTo ?? `/${chosenDepartment.department_id}/staffing/${shiftStart.toFormat("yyyy-MM-dd")}`;

	const saveChanges = async (): Promise<void> => {
		try {
			if (isLoading.updateNurse) return;

			trackEvent("save_changes_mobile", { nurseInShiftDetails: shift });

			const updatedNurse: NurseInShift = deepCloneObject(shift)!;
			updatedNurse.startTime = shiftStart;
			updatedNurse.endTime = shiftEnd;
			updatedNurse.trainingStatus = isTrainee ? NurseTrainingStatus.Trainee : NurseTrainingStatus.Regular;
			updatedNurse.charge = isCharge;
			updatedNurse.location = selectedRoom;
			updatedNurse.jobId = selectedJobId;
			updatedNurse.jobName = nurse?.jobs?.find((j) => j.id === selectedJobId)?.displayName ?? "";
			const { date, shiftType } = scheduleDayOf(shift);
			await updateNurse({ nurse: updatedNurse, date, shiftType });
			updateNurseTimes(updatedNurse.id, date, shiftType, shiftStart, shiftEnd, false);
			// New hours can move the shift to another schedule day.
			await afterScheduleWrite({ dates: [date, scheduleDayOf(updatedNurse).date] });
			navigate(getReturnPath());
			showSnackbar(
				t("nurse.updated.success", {
					nurseName: shift.name,
				}),
				"success",
			);
		} catch (e) {
			showErrorSnackbar("Failed to save changes", { nurseId: shift.id }, e);
			return;
		}
	};

	return (
		<Box sx={PAGE_SX}>
			<CommonHeader title={"Edit Shift"} onClose={() => navigate(getReturnPath())} />

			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					overflowY: "auto",
					height: "calc(100% - 40px)",
				}}
			>
				<Box sx={{ px: 2, py: 1.5, height: "fit-content" }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							mb: 2,
						}}
					>
						<Box
							sx={{
								width: "100%",
							}}
						>
							<MobileNurseItem nurse={shift} background />
						</Box>
					</Box>

					<ShiftDataRoomList room={selectedRoom} setRoom={setSelectedRoom} />

					{hasMultipleJobs && (
						<Box sx={{ mb: 2 }}>
							<Typography sx={{ color: "primary.main", mb: 0.5, fontSize: "13px" }}>Job</Typography>
							<JobSelector
								jobs={nurse?.jobs ?? []}
								selectedJobIds={selectedJobId ? [selectedJobId] : []}
								onSelectedJobsChanged={(ids) => setSelectedJobId(ids[0])}
								singleSelect
								hideAllOption
								variant="dropdown"
							/>
						</Box>
					)}

					<Box sx={{ mb: 2 }}>
						<Typography
							sx={{
								color: "primary.main",
								mb: 0.5,
								fontSize: "13px",
							}}
						>
							Choose Hours
						</Typography>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								border: `1px solid ${NEUTRAL[300]}`,
								borderRadius: "4px",
								p: 1,
								height: "40px",
								justifyContent: "space-between",
							}}
							onClick={(e) => {
								setAnchorRef(e.currentTarget);
								setIsEditingTime(true);
							}}
						>
							<Typography>
								{shiftStart.toFormat("h:mm a")} - {shiftEnd.toFormat("h:mm a")}
							</Typography>
							<AccessTimeIcon sx={{ color: "primary.main" }} />
						</Box>
						{isEditingTime && (
							<EditTimePopover
								role={nurse ? inverseRolesMap[nurse.role] : undefined}
								jobId={selectedJobId}
								startTime={shiftStart}
								endTime={shiftEnd}
								onTimeChange={({ newStartTime, newEndTime }) => {
									trackEvent("change_shift_times_mobile", {
										shiftStart,
										shiftEnd,
										shiftNo,
										nurse: shift,
									});
									setShiftStart(newStartTime);
									setShiftEnd(newEndTime);
								}}
								onClose={() => setIsEditingTime(false)}
								anchorEl={anchorRef!}
								isActual={false}
							/>
						)}
					</Box>

					<Box sx={{ gap: 2, flexDirection: "column", display: "flex" }}>
						<FormControlLabel
							control={
								<Checkbox
									checked={isCharge}
									onChange={({ target }) => setIsCharge(target.checked)}
									sx={{
										color: "primary.main",
										padding: "4px",
										"&.Mui-checked": {
											color: SHIFT_NIGHT_FALLBACK,
										},
									}}
								/>
							}
							label={<Typography sx={{ color: INK.SECONDARY, fontSize: "14px" }}>Make Charge</Typography>}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={isTrainee}
									onChange={({ target }) => setIsTrainee(target.checked)}
									sx={{
										color: "primary.main",
										padding: "4px",
										"&.Mui-checked": {
											color: SHIFT_NIGHT_FALLBACK,
										},
									}}
								/>
							}
							label={<Typography sx={{ color: INK.SECONDARY, fontSize: "14px" }}>Make Trainee</Typography>}
						/>
					</Box>

					<ShiftDataNurseRemoval
						nurse={shift}
						absenceMessage={absenceMessage}
						setAbsenceMessage={setAbsenceMessage}
						date={shift.startTime}
						isRemoveButtonEnabled={isRemoveButtonEnabled}
					/>
					{!isRemoveButtonEnabled && (
						<Button
							fullWidth
							variant="contained"
							onClick={saveChanges}
							disabled={isLoading.removeNurse || isLoading.updateNurse}
							sx={{
								bgcolor: "primary.main",
								color: "white",
								textTransform: "none",
								"&:hover": {
									bgcolor: "primary.dark",
								},
								height: "40px",
								fontSize: "14px",
							}}
						>
							Save Changes
						</Button>
					)}
				</Box>
			</Box>
		</Box>
	);
};
